<template>
  <div class="navbar-event">
    <div class="event-header-wrapper">
      <div class="event-header">
        <div class="header-title">
          <GBadge v-if="event.publish_state === PUBLISH_STATES.DRAFT" variant="dark" class="mr-1">
            {{ $t('labels.event.draft') }}
          </GBadge>
          <GBadge v-else-if="event.is_template" variant="dark">
            {{ $tc('labels.template') }}
          </GBadge>
          <span v-if="event.is_template" class="event-subtitle">{{ event.template_title }}</span>
          <div v-else>
            <span v-if="event.title" class="event-title">{{ event.title }}</span>
            <span v-if="event.subtitle" class="event-subtitle">{{ event.subtitle }}</span>
          </div>
        </div>
        <div class="header-info">
          <span v-if="event.when && !event.is_template" class="meta event-date">
            <Icon type="calendar" size="16px" :color="styles.colorGrayLight" />
            {{ formattedDate(event.when, event.local_timezone) }}
          </span>
          <span v-if="event.location" class="meta event-location">
            <Icon type="pin" size="16px" :color="styles.colorGrayLight" />
            {{ event.location }}
          </span>
        </div>
      </div>
    </div>

    <div class="navbar_ctrls_wrapper">
      <ul v-if="!$route.meta?.isCreating && !!event" class="navbar-event-items" data-test-event-nav>
        <li v-if="!event.is_template" class="nav-item">
          <router-link :to="{ name: 'event-reports', params: { slug: event.slug } }">
            <Icon type="reports" size="24px" class="icon" />
            <span class="nav-item-text">
              {{ $t('navigation.reports') }}
            </span>
          </router-link>
        </li>
        <li v-if="hasPermission('shop.read')" class="nav-item">
          <router-link :to="{ name: 'shops', params: { slug: event.slug } }" data-test-shops-link>
            <Icon type="shops" size="24px" class="icon" />
            <span class="nav-item-text lg">{{ $t('navigation.shops') }}</span>
          </router-link>
        </li>
        <li v-if="hasPermission('ticket.privilegetype.read')" class="nav-item">
          <router-link :to="{ name: 'pools', params: { slug: event.slug } }" data-test-pools-link>
            <Icon type="pools" size="24px" class="icon" />
            <span class="nav-item-text lg">
              {{ $t('navigation.pools') }}
            </span>
          </router-link>
        </li>
        <li v-if="!event.is_template && hasPermission('attendee.read')" class="nav-item">
          <router-link :to="{ name: 'event-customers', params: { slug: event.slug } }" data-test-customers-link>
            <Icon type="attendees" size="24px" class="icon" />
            <span class="nav-item-text">
              {{ $t('navigation.customers') }}
            </span>
          </router-link>
        </li>
        <li v-if="showOrdersButton" class="nav-item">
          <router-link :to="{ name: 'event-orders-overview', params: { slug: event.slug } }" data-test-orders-link>
            <Icon type="ticket-combi" size="24px" class="icon" />
            <span class="nav-item-text">
              {{ $t('navigation.orders') }}
            </span>
          </router-link>
        </li>
        <li v-if="showReservationsButton" class="nav-item">
          <router-link :to="{ name: 'event-reservations', params: { slug: event.slug } }" data-test-reservations-link>
            <Icon type="reservations" size="24px" class="icon" />
            <span class="nav-item-text">
              {{ $t('navigation.reservations') }}
            </span>
          </router-link>
        </li>
        <li v-if="hasSeating && hasPermission('seating.read')" class="nav-item">
          <router-link :to="{ name: 'seating-overview', params: { slug: event.slug } }" data-test-seating-link>
            <Icon type="seating" size="24px" class="icon" />
            <span class="nav-item-text">
              {{ $t('navigation.seating') }}
            </span>
          </router-link>
        </li>
        <li v-if="hasPermission('event.event.scan')" class="nav-item">
          <router-link :to="{ name: 'event-scanning', params: { slug: event.slug } }" data-test-scanning-link>
            <Icon type="scanning" size="24px" class="icon" />
            <span class="nav-item-text lg">
              {{ $t('navigation.scanning') }}
            </span>
          </router-link>
        </li>
        <li v-if="hasPermission('event.event.dashboard_scan')" class="nav-item">
          <router-link :to="{ name: 'event-checkin', params: { slug: event.slug } }" data-test-scanning-link>
            <FeatureFlagIndicator tag="event-checkin" />
            <Icon type="checkin" size="24px" class="icon" />
            <span class="nav-item-text lg">
              {{ $t('navigation.checkin') }}
            </span>
          </router-link>
        </li>
        <li class="nav-item">
          <router-link :to="{ name: 'general', params: { slug: event.slug } }" data-test-manage-link>
            <Icon type="settings" size="24px" class="icon" />
            <span class="nav-item-text">
              {{ $t('navigation.manage') }}
            </span>
          </router-link>
        </li>
        <li v-if="!!event.template" class="nav-item nav-item-template">
          <router-link :to="{ name: 'general', params: { slug: event.template } }" data-test-manage-from-template-link>
            <Icon type="templates" size="24px" class="icon" />
            <span class="nav-item-text"> Manage from template </span>
          </router-link>
        </li>
        <li v-if="event.is_template" class="nav-item-sync">
          <router-link
            :to="{ name: 'sync', params: { slug: event.slug } }"
            class="bg-transparent hover:bg-transparent disabled:pointer-events-none px-4"
            :class="isSynced ? 'link text-success' : 'link text-danger'"
            :disabled="!hasPermission('event.write')"
            data-test-sync-button
          >
            <Icon :color="isSynced ? styles.colorPrimary : styles.colorDanger" type="sync" size="20px" />
            <span class="nav-item-text">
              {{ $t(isSynced ? 'views.templates.status.synced' : 'navigation.sync') }}
            </span>
          </router-link>
        </li>
      </ul>

      <div v-if="!event.is_template" class="cashier_btn_wrapper">
        <div id="sell-ticket-btn">
          <GButton
            v-if="hasPermission('ticket.cashier.create')"
            variant="primary"
            class="sellTicketButton"
            :disabled="disableIssueButton"
            data-test-sell-ticket-btn
            @click="navigateToCashier"
          >
            <Icon type="plus" size="20px" color="white" />
            <span class="cashier_btn_label">{{ $t('views.events.event.attendees.cashier.action') }}</span>
          </GButton>
        </div>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
  import styles from '@/assets/styles/exports.module.scss';
  import PermissionMixin from '@/mixins/PermissionsMixin';
  import Icon from '@/components/Icon.vue';
  import FeatureFlagIndicator from '@/components/FeatureFlagIndicator.vue';

  import { PUBLISH_STATES } from '@/helpers/constants';
  import { defineComponent } from 'vue';
  import { Event } from '@/views/events/event/types';

  export default defineComponent({
    components: {
      Icon,
      FeatureFlagIndicator,
    },
    mixins: [PermissionMixin],
    props: {
      event: {
        type: Object as () => Event,
        required: true,
      },
    },
    data() {
      return {
        styles,
        PUBLISH_STATES,
      };
    },
    computed: {
      hasSeating() {
        return !!this.event.seating;
      },
      isSynced() {
        return this.event.publish_state === PUBLISH_STATES.TEMPLATE_SYNCED;
      },
      showReservationsButton() {
        return !this.event.is_template && this.hasPermission('reservation.write');
      },
      showOrdersButton() {
        const defaultPermissions = this.hasPermission('attendee.read') && this.hasPermission('order.read');

        if (!this.event.is_template && defaultPermissions) {
          return true;
        }

        return false;
      },
      disableIssueButton() {
        const cashierRoutes = ['cashier-select', 'cashier-issue', 'reservation-create'];
        return cashierRoutes.includes(this.$route.name || '');
      },
    },
    methods: {
      formattedDate(date: Date, zone: string) {
        return `${this.$moment.tz(date, zone).format('L')}`;
      },
      navigateToCashier() {
        this.$router.push({
          name: 'cashier-select',
        });
      },
    },
  });
</script>

<style lang="scss" scoped>
  .navbar-event {
    display: flex;
    flex-direction: column;
    background: $color-white;
    border-bottom: 1px solid $color-border;
    align-items: center;

    @media (min-width: $tablet) {
      flex-direction: row;
    }
  }

  .event-header-wrapper {
    position: relative;

    @media (max-width: $tablet) {
      width: 100%;
    }
  }

  .event-header {
    display: flex;
    white-space: nowrap;
    overflow-x: auto;
    border-bottom: 1px solid $color-gray-300;
    padding: 0.5rem 0.5rem 0.5rem 0.75rem;

    @media (min-width: $tablet) {
      flex-direction: column;
      border-right: 1px solid $color-gray-300;
      border-bottom: none;
      max-width: 13rem;
      overflow-x: hidden;
      cursor: pointer;

      &:active,
      &:focus {
        max-width: inherit;
      }

      &:after {
        position: absolute;
        top: 0;
        right: 1px;
        width: 2rem;
        height: 100%;
        content: '';
        background: linear-gradient(
          90deg,
          rgba($color-white, 0) 0%,
          rgba($color-white, 1) 80%,
          rgba($color-white, 1) 100%
        );
        display: block;
      }
    }
  }

  .header-title {
    display: flex;
    white-space: nowrap;
    align-items: center;
  }

  .event-title,
  .event-subtitle {
    margin-right: 0.5rem;
    font-weight: $weight-semibold;
  }

  .event-title {
    color: $color-tertiary;
  }

  .header-info {
    display: flex;
    align-items: center;
    font-size: $size-tiny;
  }

  .event-date,
  .event-location {
    margin-right: 0.5rem;
  }

  .meta {
    display: flex;
    align-items: center;
    color: $color-gray-500;
  }

  .navbar_ctrls_wrapper {
    display: flex;
    flex-wrap: nowrap;
    width: 100%;
  }

  .navbar-event-items {
    overflow-x: auto;
    flex: 1;
    display: flex;
    align-items: center;
    margin: 0 0.5rem 0 0.75rem;
    padding: 0;
    list-style: none;
  }

  // This line is actually being used and eslint is complaining about it
  // eslint-disable-next-line vue-scoped-css/no-unused-selector
  .nav-item a {
    display: flex;
    align-items: center;
    white-space: nowrap;
    color: $color-gray-400;
    padding: 0.25rem;
    margin-right: 0.25rem;

    .icon :deep(path) {
      fill: $color-gray-400;
    }

    &:hover,
    &.is-active,
    &.is-parent-active {
      color: $color-primary;

      .icon :deep(path) {
        fill: $color-primary;
      }
    }

    &.right {
      margin-left: auto;
      margin-right: 0;
    }
  }

  .nav-item-text,
  .cashier_btn_label {
    display: none;
    padding-left: 0.15rem;

    @media (min-width: calc($desktop + 240px)) {
      display: inline-block;
    }
  }

  .nav-item-sync,
  .nav-item-template {
    margin-left: auto;
    margin-right: 0.5rem;
  }
  .sellTicketButton {
    margin-right: 1rem;
    @media (max-width: $tablet) {
      padding: initial;
    }
  }
  .cashier_btn_wrapper {
    display: flex;
    flex-direction: row;
    align-items: center;
    justify-self: flex-end;
  }
</style>
